// Third-party libraries
import React, { Component, Fragment } from "react";
import { Link } from "react-router-dom";
import { intComma, ordinal } from "humanize-plus";

// Project dependencies
import FileNotFound from "./FileNotFound";
import PlayerChart from "../components/PlayerChart";
import { Invite, LoadingBar } from "../components/widgets";
import { getHeaders, playerUrl, selfUrl, errorHandler } from "../apihelpers";
import { Meta } from "../utilities";
import BetHistory from "../components/BetHistory";
import PlayerContestChart from "../components/PlayerContestChart";
import WapIcon from "../svg/WapIcon";
import variables from "../styleVariables";
import PencilIcon from "../svg/PencilIcon";
import TrophyIcon from "../svg/TrophyIcon";

class Player extends Component {
  state = {
    digestChanged: false,
    reminderChanged: false,
    isOwner: false,
    notFound: false,
    edits: {
      screen_name: {
        editing: false,
        new: "",
        error: null,
      },
      location: {
        editing: false,
        new: "",
        error: null,
      },
      twitter: {
        editing: false,
        new: "@",
        error: null,
      },
      url: {
        editing: false,
        new: "",
        error: null,
      },
    },
  };

  constructor(props) {
    super(props);
    this.toggleField = this.toggleField.bind(this);
    this.toggleDigest = this.toggleDigest.bind(this);
    this.toggleReminders = this.toggleReminders.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.updateField = this.updateField.bind(this);
  }

  handleChange(event) {
    const target = event.target;
    let value = target.value;
    const name = target.name;
    let edits = Object.assign({}, this.state.edits);

    if (name === "location" && value.length > 0) {
      value = value.substring(0, 30);
    }

    if (
      name === "url" &&
      value.length > 4 &&
      value.substring(0, 4) !== "http"
    ) {
      value = "http://" + value;
    }

    if (name === "twitter" && value.length > 0) {
      value =
        "@" +
        value.toLowerCase().replace("@", "").replace(" ", "_").substring(0, 40);
      if (value === "@") {
        value = "";
      }
    }

    if (name === "screen_name") {
      edits.screen_name.new = value.substring(0, 30);
      if (value.length < 1) {
        edits.screen_name.error = "Your name must not be blank.";
      } else {
        edits.screen_name.error = null;
      }
    }
    edits[name].new = value;

    this.setState({
      edits,
    });
  }

  updateField(event) {
    const headers = getHeaders();
    this.setState({ loading: true, success: false });
    fetch(selfUrl, {
      method: "POST",
      headers,
      body: JSON.stringify({
        field: event.target.name,
        new: this.state.edits[event.target.name].new,
      }),
    })
      .then(errorHandler)
      .then((response) => {
        let edits = Object.assign({}, this.state.edits);
        if (response.error === false) {
          window.gtag("event", "change", {
            event_category: "profile",
            event_label: `${response.field}: ${this.state[response.field]} to ${
              response.new.length > 0 ? response.new : "blank"
            }`,
          });
          edits[response.field].editing = false;
          edits[response.field].error = null;
          this.setState({ edits, [response.field]: response.new });
          this.getPlayerData();
        } else {
          edits[response.field].error = response.message;
          this.setState({ edits });
        }
      });
    event.preventDefault();
  }

  toggleField(field) {
    let edits = Object.assign({}, this.state.edits);
    edits[field].editing = true;
    this.setState({ edits });
  }

  toggleDigest(event) {
    const headers = getHeaders();
    this.setState({
      digestChanged: true,
    });
    fetch(selfUrl, {
      method: "POST",
      headers,
      body: JSON.stringify({
        field: "is_subscribed_to_digest",
        new: !this.state.digest,
      }),
    })
      .then(errorHandler)
      .then((response) => {
        this.setState({ digest: response.new }, () => {
          window.gtag(
            "event",
            this.state.digest ? "subscribe" : "unsubscribe",
            {
              event_category: "digest",
              event_label: "via profile page",
            }
          );
        });
        this.digestTimeout = setTimeout(() => {
          this.setState({ digestChanged: false });
        }, 3000);
      });

    event.preventDefault();
  }

  toggleReminders(event) {
    const headers = getHeaders();
    this.setState({
      reminderChanged: true,
    });
    fetch(selfUrl, {
      method: "POST",
      headers,
      body: JSON.stringify({
        field: "is_subscribed_to_reminders",
        new: !this.state.reminders,
      }),
    })
      .then(errorHandler)
      .then((response) => {
        this.setState({ reminders: response.new }, () => {
          window.gtag(
            "event",
            this.state.reminders ? "subscribe" : "unsubscribe",
            {
              event_category: "reminders",
              event_label: "via profile page",
            }
          );
        });
        this.digestTimeout = setTimeout(() => {
          this.setState({ reminderChanged: false });
        }, 3000);
      });

    event.preventDefault();
  }

  getPlayerData() {
    const headers = getHeaders();
    fetch(playerUrl + "/" + this.props.match.params.handle + "?" + Date.now(), {
      method: "GET",
      headers,
    })
      .then(errorHandler)
      .then((data) => {
        let edits = Object.assign({}, this.state.edits);
        edits.url.new = data.url;
        edits.location.new = data.location;
        edits.screen_name.new = data.screen_name;
        edits.twitter.new = data.twitter;
        this.setState({
          ...data,
          notFound: false,
          isOwner: data.id === this.props.self.id,
        });
      })
      .catch((error) => {
        console.log(error);
        this.setState({ notFound: true });
      });
  }

  componentDidMount() {
    this.getPlayerData();
    if (this.props.self) {
      this.setState({
        digest: this.props.self.is_subscribed_to_digest,
        reminders: this.props.self.is_subscribed_to_reminders,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.getPlayerData();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.digestTimeout);
  }

  render() {
    if (this.state.notFound) {
      return <FileNotFound />;
    } else if (this.state.screen_name) {
      const betsAll = [...this.state.losers, ...this.state.winners];
      console.log(this.state);
      const longShotWinners = this.state.winners.filter(
        (bet) => bet.pre_payout > 21
      );
      const longShotLosers = this.state.losers.filter(
        (bet) => bet.pre_payout > 21
      );
      const longShotAll = [...longShotWinners, ...longShotLosers];

      const coinFlipWinners = this.state.winners.filter(
        (bet) => bet.pre_payout > 1.5 && bet.pre_payout <= 2.5
      );
      const coinFlipLosers = this.state.losers.filter(
        (bet) => bet.pre_payout > 1.5 && bet.pre_payout <= 2.5
      );
      const coinFlipAll = [...coinFlipWinners, ...coinFlipLosers];

      const safeWinners = this.state.winners.filter(
        (bet) => bet.pre_payout <= 1.5
      );
      const safeLosers = this.state.losers.filter(
        (bet) => bet.pre_payout <= 1.5
      );
      const safeAll = [...safeWinners, ...safeLosers];

      return (
        <Fragment>
          <Meta
            title={`Rouleur Derby: ${this.state.screen_name}`}
            description={`${this.state.screen_name} is an important part of what makes Rouleur Derby so great.`}
          />
          <link rel="canonical" href={`/${this.state.handle}`} />

          {this.state.edits.screen_name.editing ? (
            <Fragment>
              {this.state.edits.screen_name.error && (
                <p className="error">{this.state.edits.screen_name.error}</p>
              )}
              <form
                onSubmit={this.updateField}
                name="screen_name"
                className="player__name-edit-form"
              >
                <input
                  type="text"
                  value={this.state.edits.screen_name.new}
                  name="screen_name"
                  onChange={this.handleChange}
                />
                <button
                  disabled={this.state.edits.screen_name.error !== null}
                  className="pill__save"
                >
                  Save
                </button>
              </form>
            </Fragment>
          ) : (
            <h1>
              {this.state.screen_name}{" "}
              {this.state.isOwner && (
                <button
                  className="player__name-edit"
                  onClick={() => this.toggleField("screen_name")}
                >
                  <PencilIcon color={variables.colorPrimary} />
                </button>
              )}
            </h1>
          )}

          {(this.state.location ||
            this.state.twitter ||
            this.state.url ||
            this.state.isOwner ||
            (this.state.trophies && this.state.trophies.length > 0)) && (
            <ul className="player_details">
              {this.state.podium_member && (
                <li>
                  <a
                    href="https://www.wideanglepodium.com"
                    rel="noopener noreferrer"
                    target="_blank"
                    className="player__pill player__pill--wap"
                  >
                    <WapIcon color="#ffffff" foreground={variables.colorWap} />
                    Wide Angle Podium member
                  </a>
                </li>
              )}
              {(this.state.location || this.state.isOwner) && (
                <li>
                  <span
                    className={`player__pill ${
                      this.state.edits.location.editing &&
                      "player__pill--editing"
                    }`}
                  >
                    {this.state.edits.location.editing === false &&
                      (this.state.location || <em>Location</em>)}
                    {this.state.isOwner &&
                      (this.state.edits.location.editing ? (
                        <form onSubmit={this.updateField} name="location">
                          <input
                            type="text"
                            value={this.state.edits.location.new}
                            name="location"
                            onChange={this.handleChange}
                          />
                          <button className="pill__save">Save</button>
                        </form>
                      ) : (
                        <button
                          className="pill__edit"
                          onClick={() => this.toggleField("location")}
                        >
                          <PencilIcon color="#fff" />
                        </button>
                      ))}
                  </span>
                </li>
              )}

              {(this.state.twitter || this.state.isOwner) && (
                <li>
                  {this.state.isOwner ? (
                    <span
                      className={`player__pill player__pill--primary-color ${
                        this.state.edits.twitter.editing &&
                        "player__pill--editing"
                      }`}
                    >
                      {this.state.edits.twitter.editing ? (
                        <form onSubmit={this.updateField} name="twitter">
                          <input
                            type="text"
                            value={this.state.edits.twitter.new}
                            name="twitter"
                            onChange={this.handleChange}
                          />
                          <button className="pill__save">Save</button>
                        </form>
                      ) : (
                        <Fragment>
                          {this.state.twitter || <em>Twitter handle</em>}
                          <button
                            className="pill__edit"
                            onClick={() => this.toggleField("twitter")}
                          >
                            <PencilIcon color="#fff" />
                          </button>
                        </Fragment>
                      )}
                    </span>
                  ) : (
                    <a
                      className="player__pill"
                      href={`http://www.twitter.com/${this.state.twitter.replace(
                        "@",
                        ""
                      )}`}
                    >
                      {this.state.twitter}
                    </a>
                  )}
                </li>
              )}

              {(this.state.url || this.state.isOwner) && (
                <li>
                  {this.state.isOwner ? (
                    <span
                      className={`player__pill player__pill--primary-color ${
                        this.state.edits.url.editing && "player__pill--editing"
                      }`}
                    >
                      {this.state.edits.url.editing ? (
                        <form onSubmit={this.updateField} name="url">
                          <input
                            className="pill__input--wide"
                            type="text"
                            value={this.state.edits.url.new}
                            name="url"
                            onChange={this.handleChange}
                          />
                          <button className="pill__save">Save</button>
                        </form>
                      ) : (
                        <Fragment>
                          {this.state.url || <em>Home page</em>}
                          <button
                            className="pill__edit"
                            onClick={() => this.toggleField("url")}
                          >
                            <PencilIcon color="#fff" />
                          </button>
                        </Fragment>
                      )}
                    </span>
                  ) : (
                    <a className="player__pill" href={this.state.url}>
                      {this.state.url}
                    </a>
                  )}
                </li>
              )}

              {this.state.trophies &&
                this.state.trophies.length > 0 &&
                this.state.trophies.map((trophy, i) => (
                  <li key={i}>
                    <span className="player__pill player__pill--with-svg player__pill--trophy">
                      <TrophyIcon color={variables.colorPrimary} />
                      {trophy.title}
                    </span>
                  </li>
                ))}
            </ul>
          )}

          {this.state.isOwner && (
            <>
              <fieldset
                className={`player__digest preference--checkbox ${
                  this.state.digestChanged ? "active" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={this.state.digest}
                  id="digest-checkbox"
                  onChange={this.toggleDigest}
                ></input>

                <label
                  htmlFor="digest-checkbox"
                  className="label--regular"
                  aria-hidden={this.state.digestChanged}
                >
                  Subscribe to daily digest of results (sent only on days with
                  races)
                </label>
                <label
                  className="preference-label__change"
                  aria-hidden={!this.state.digestChanged}
                >
                  Got it. Your preference has been changed.
                </label>
              </fieldset>

              <fieldset
                className={`player__reminders preference--checkbox ${
                  this.state.reminderChanged ? "active" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={this.state.reminders}
                  id="reminder-checkbox"
                  onChange={this.toggleReminders}
                ></input>

                <label
                  htmlFor="reminder-checkbox"
                  className="label--regular"
                  aria-hidden={this.state.reminderChanged}
                >
                  Get reminders four hours before betting closes
                </label>
                <label
                  className="preference-label__change"
                  aria-hidden={!this.state.reminderChanged}
                >
                  Got it. Your preference has been changed.
                </label>
              </fieldset>
            </>
          )}

          {this.state.isOwner && this.props.self.invitations_left > 0 && (
            <Invite player={this.props.self} />
          )}

          <div className="player__row ">
            <div className="player__col--50">
              <h2 className="player__label">Points</h2>
              <span className="player__stat player__stat--points">
                {intComma(this.state.points)}
              </span>
            </div>
            <div className="player__col--50">
              <div className="player__row">
                <div className="player__col--50">
                  <h2 className="player__label">Rank</h2>
                  <span className="player__stat">
                    {intComma(this.state.rank[0])}
                    <span className="player__stat-memo">
                      out of {intComma(this.state.rank[1])}
                    </span>
                  </span>
                </div>
                <div className="player__col--50">
                  <h2 className="player__label">Bets</h2>
                  <span className="player__stat">
                    {intComma(this.state.bets)}
                  </span>
                </div>
              </div>
              <div className="player__row">
                <div className="player__col--50">
                  <h2 className="player__label">Current Action</h2>
                  <span className="player__stat">
                    {intComma(this.state.current_action)}
                  </span>
                </div>
                <div className="player__col--50">
                  <h2 className="player__label">Current Bets</h2>
                  <span className="player__stat">
                    {intComma(this.state.current_bets)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {this.state.history.length >= 2 && (
            <div className="player__row">
              <div className="player__col--100">
                <h2 className="player__label">History</h2>
                <PlayerChart
                  history={this.state.history}
                  handle={this.state.handle}
                  makePNG={false}
                  overall_max={this.state.overall_max}
                />
              </div>
            </div>
          )}

          <PlayerContestChart
            results={this.state.contest_results}
            rois={this.state.contest_rois}
          />

          <div className="player__row player__row--no-margin-bottom">
            <div className="player__col--100">
              <h2 className="player__label">Track Record</h2>
            </div>
          </div>

          <div className="player__row">
            <div className="player__col--25">
              <h3 className="player__label--sub">Overall</h3>

              <p className="player__label--result">
                {this.state.isOwner ? "You" : this.state.screen_name}
              </p>
              <ResultChart
                winnerCount={this.state.winners.length}
                totalCount={betsAll.length}
              />

              <p className="player__label--result">Everyone</p>
              <ResultChart
                winnerCount={this.state.all_winners_count}
                totalCount={
                  this.state.all_winners_count + this.state.all_losers_count
                }
                show="pct"
                who="everyone"
              />
            </div>
            <div className="player__col--25">
              <h3 className="player__label--sub">Safe bets</h3>
              <p className="player__label--result">
                {this.state.isOwner ? "You" : this.state.screen_name}
              </p>
              <ResultChart
                winnerCount={safeWinners.length}
                totalCount={safeAll.length}
              />

              <p className="player__label--result">Everyone</p>
              <ResultChart
                winnerCount={this.state.safe_winners_count}
                totalCount={
                  this.state.safe_winners_count + this.state.safe_losers_count
                }
                show="pct"
                who="everyone"
              />

              <p className="player__explainer">
                <em>Odds shorter than 1/2</em>
              </p>
            </div>
            <div className="player__col--25">
              <h3 className="player__label--sub">Coin flips</h3>
              <p className="player__label--result">
                {this.state.isOwner ? "You" : this.state.screen_name}
              </p>
              <ResultChart
                winnerCount={coinFlipWinners.length}
                totalCount={coinFlipAll.length}
              />
              <p className="player__label--result">Everyone</p>
              <ResultChart
                winnerCount={this.state.coinflip_winners_count}
                totalCount={
                  this.state.coinflip_winners_count +
                  this.state.coinflip_losers_count
                }
                show="pct"
                who="everyone"
              />
              <p className="player__explainer">
                <em>Odds between 1/2 and 3/2</em>
              </p>
            </div>
            <div className="player__col--25">
              <h3 className="player__label--sub">Long shots</h3>
              <p className="player__label--result">
                {this.owner ? "You" : this.state.screen_name}
              </p>
              <ResultChart
                winnerCount={longShotWinners.length}
                totalCount={longShotAll.length}
              />
              <p className="player__label--result">Everyone</p>
              <ResultChart
                winnerCount={this.state.longshot_winners_count}
                totalCount={
                  this.state.longshot_winners_count +
                  this.state.longshot_losers_count
                }
                show="pct"
                who="everyone"
              />
              <p className="player__explainer">
                <em>Odds longer than 20/1</em>
              </p>
            </div>
          </div>
          {this.state.winners.length + this.state.losers.length > 2 && (
            <BetHistory
              winners={this.state.winners}
              losers={this.state.losers}
            />
          )}
          <div className="player__row">
            <div className="player__col--50">
              <h2 className="player__label">Biggest Winners</h2>
              {this.state.winners.length > 0 ? (
                <ul className="player__list">
                  {this.state.winners.slice(0, 5).map((bet, i) => (
                    <WinnersLosersItem bet={bet} key={i} />
                  ))}
                </ul>
              ) : (
                <p>
                  {this.state.isOwner ? "You have" : "This player has"} not won
                  any bets yet.
                </p>
              )}
            </div>
            <div className="player__col--50">
              <h2 className="player__label">Biggest Losers</h2>
              {this.state.losers.length > 0 ? (
                <ul className="player__list">
                  {this.state.losers.slice(0, 5).map((bet, i) => (
                    <WinnersLosersItem bet={bet} key={i} />
                  ))}
                </ul>
              ) : (
                <p>
                  {this.state.isOwner ? "You have" : "This player has"} not lost
                  any bets yet.
                </p>
              )}
            </div>
          </div>

          <div className="player__row">
            <div className="player__col--25">
              <h2 className="player__label">Top Nemeses</h2>
              <p className="player__explainer">
                Nemeses have <em>won</em> points from{" "}
                {this.state.isOwner ? "you" : this.state.screen_name}.
              </p>
              {Object.values(this.state.nemeses).length === 0 ? (
                <p>This player has no nemeses … yet.</p>
              ) : (
                <ul className="player__list">
                  {Object.values(this.state.nemeses)
                    .sort((a, b) => b.amount - a.amount)
                    .slice(0, 5)
                    .map((nemesis, i) => (
                      <li className="player__list-item" key={i}>
                        <span className="player__list-item-text">
                          <Link to={`/${nemesis.handle}`}>
                            {nemesis.screen_name}
                          </Link>
                        </span>
                        <span className="player__list-item-figure">
                          {intComma(nemesis.amount >= 1 ? nemesis.amount : 1)}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            <div className="player__col--25">
              <h2 className="player__label">Top Victims</h2>
              <p className="player__explainer">
                Victims have <em>lost</em> points to{" "}
                {this.state.isOwner ? "you" : this.state.screen_name}.
              </p>
              {Object.values(this.state.victims).length === 0 ? (
                <p>This player has no victims … yet.</p>
              ) : (
                <ul className="player__list">
                  {Object.values(this.state.victims)
                    .sort((a, b) => b.amount - a.amount)
                    .slice(0, 5)
                    .map((victim, i) => (
                      <li className="player__list-item" key={i}>
                        <span className="player__list-item-text">
                          <Link to={`/${victim.handle}`}>
                            {victim.screen_name}
                          </Link>
                        </span>
                        <span className="player__list-item-figure">
                          {intComma(victim.amount >= 1 ? victim.amount : 1)}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            <div className="player__col--25">
              <h2 className="player__label">Leagues</h2>
              {this.state.leagues.length === 0 ? (
                <p className="player__explainer">
                  {this.state.isOwner ? "You are" : "This player is"} not in any{" "}
                  <Link to="/leagues">leagues</Link> yet.
                </p>
              ) : (
                <ul className="player__list">
                  {this.state.leagues.map((league, i) => (
                    <li key={i} className="player__list-item">
                      <Link to={`/league/${league.slug}`}>{league.name}</Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {this.state.seasons.length > 0 && (
              <div className="player__col--25">
                <h2 className="player__label">Previous seasons</h2>
                <ul className="player__list player__list--ruled">
                  {this.state.seasons.reverse().map((season, i) => (
                    <li key={i} className="player__list-item">
                      {season.season}{" "}
                      <span
                        className="player__list-item-figure"
                        style={{ flexGrow: 1, width: "auto" }}
                      >
                        {intComma(season.points)}
                        <br />
                        <em>
                          {ordinal(season.rank)} out of {season.out_of}
                        </em>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Fragment>
      );
    } else {
      return <LoadingBar />;
    }
  }
}

function Bar({ who, width }) {
  return (
    <div
      className={`bar__outer ${
        who === "everyone" ? "bar__outer--secondary" : ""
      }`}
    >
      <div
        className={`bar__inner ${
          who === "everyone" ? "bar__inner--secondary" : ""
        }`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function ResultChart({ winnerCount, totalCount, show, who }) {
  return (
    <div className="player__result-chart">
      <span className="player__result-chart-label">
        {show === "pct"
          ? totalCount > 0
            ? `${Math.round((winnerCount / totalCount) * 100, 1)}%`
            : "0%"
          : `${intComma(winnerCount)}/${intComma(totalCount)}`}
      </span>
      <Bar
        who={who}
        width={totalCount > 0 ? (winnerCount / totalCount) * 100 : 0}
      />
    </div>
  );
}

function WinnersLosersItem({ bet }) {
  return (
    <li className="player__winner-loser-item player__list-item ">
      <span className="player__winner-loser-amount">
        {intComma(bet.profit > 0 ? bet.profit : `-${bet.amount}`)}
      </span>{" "}
      <span className="player__list-item-text">
        {bet.profit > 0 ? `${intComma(bet.amount)} on ` : ""}
        {bet.selection.name} in{" "}
        <Link to={`/prop/${bet.proposition.slug}`}>{bet.proposition.name}</Link>{" "}
        ({bet.odds[0]}/{bet.odds[1]})
      </span>
    </li>
  );
}

export default Player;
